import { lazy } from "react";
import { Navigate, createBrowserRouter } from "react-router-dom";
import MainLayout from "../components/layout/MainLayout";
import ProtectedRoute from "../components/admin/ProtectedRoute";
import PostPage from "../features/blogs/pages/PostPage";
import BlogOverviewPage from "../features/blogs/pages/BlogOverviewPage";
import { ComingSoonPage } from "../components/common/ComingSoonPage";
import NotFoundPage from "../components/common/NotFoundPage";

const LandingPage = lazy(() => import("../features/static/LandingPage"));
const BlogsPage = lazy(() => import("../features/blogs/pages/BlogsPage"));
const BlogReaderLayout = lazy(() => import("../features/blogs/pages/BlogReaderLayout"));
const AboutPage = lazy(() => import("../features/static/AboutPage"));
const PrivacyPage = lazy(() => import("../features/static/legal/PrivacyPage"));
const TermsPage = lazy(() => import("../features/static/legal/TermsPage"));
const EarlyAccessPage = lazy(() => import("../features/static/EarlyAccessPage"));
const CoursesPage = lazy(() => import("../features/series/pages/CoursesPage"));
const CourseDetailPage = lazy(() => import("../features/series/pages/CourseDetailPage"));

const AdminLayout = lazy(() => import("../features/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("../features/admin/DashboardPage"));
const AdminBlogs = lazy(() => import("../features/admin/BlogsPage"));
const AdminBlogEditor = lazy(() => import("../features/admin/BlogEditorPage"));
const AdminCourses = lazy(() => import("../features/admin/CoursesPage"));
const AdminCourseEditor = lazy(() => import("../features/admin/CourseFormPage"));
const AdminPostsList = lazy(() => import('../features/admin/PostsListPage'));
const AdminPostEditor = lazy(() => import("../features/admin/PostEditorPage"));
const AdminAnalytics = lazy(() => import('../features/admin/AnalyticsPage'));
const AdminInbox = lazy(() => import('../features/admin/InboxPage'));

const LoginPage = lazy(() => import("../features/auth/pages/LoginPage"));
const RegisterPage = lazy(() => import("../features/auth/pages/RegisterPage"));
const VerifyEmailPage = lazy(() => import("../features/auth/pages/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("../features/auth/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("../features/auth/pages/ResetPasswordPage"));
const AuthCompletePage = lazy(() => import("../features/auth/pages/AuthCompletePage"));
const SettingsPage = lazy(() => import("../features/auth/pages/SettingsPage"));

export const router = createBrowserRouter([
  {
    path: "/",
    element: <MainLayout />,
    children: [
      { index: true, element: <LandingPage /> },
      {
        path: "blog/:blogSlug",
        element: <BlogReaderLayout />,
        children: [
          { path: ":postSlug", element: <PostPage /> },
          { index: true, element: <BlogOverviewPage /> }
        ]
      },
      // Valid nav/linked destinations
      { path: "blogs", element: <BlogsPage /> },
      { path: "series", element: <CoursesPage /> },
      { path: "series/:courseSlug", element: <CourseDetailPage /> },
      { path: "about", element: <AboutPage /> },
      // Contact lives on the About page now; the old path keeps working and
      // lands on the form.
      { path: "contact", element: <Navigate to="/about#contact" replace /> },
      // Linked from the footer, and required by Google's OAuth consent screen
      // before the sign-in app can be published.
      { path: "privacy", element: <PrivacyPage /> },
      { path: "terms", element: <TermsPage /> },
      { path: "early-access", element: <EarlyAccessPage /> },
      // The design called this Membership. There is no payment yet, so the
      // idea lives at its honest name and the old one redirects.
      { path: "membership", element: <Navigate to="/early-access" replace /> },
      { path: "projects", element: <ComingSoonPage /> },
      { path: "tutorials", element: <ComingSoonPage /> },
      // Auth routes
      { path: "login", element: <LoginPage /> },
      { path: "register", element: <RegisterPage /> },
      { path: "verify-email", element: <VerifyEmailPage /> },
      { path: "forgot-password", element: <ForgotPasswordPage /> },
      { path: "reset-password", element: <ResetPasswordPage /> },
      { path: "auth/complete", element: <AuthCompletePage /> },
      { path: "settings", element: <SettingsPage /> },
      // Completely unknown URLs → proper 404
      { path: "*", element: <NotFoundPage /> },
      // Admin routes (protected)
      {
        path: "admin",
        element: (
          <ProtectedRoute>
          <AdminLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <AdminDashboard /> },
          { path: "blogs", element: <AdminBlogs /> },
          { path: "blogs/new", element: <AdminBlogEditor /> },
          { path: "blogs/:id", element: <AdminBlogEditor /> },
          { path: "courses", element: <AdminCourses /> },
          { path: "courses/new", element: <AdminCourseEditor /> },
          { path: "courses/:id", element: <AdminCourseEditor /> },
          { path: 'posts', element: <AdminPostsList /> },
          { path: 'posts/new', element: <AdminPostEditor /> },
          { path: 'posts/:id/edit', element: <AdminPostEditor /> },
          { path: 'analytics', element: <AdminAnalytics /> },
          { path: 'inbox', element: <AdminInbox /> }
        ]
      }
    ]
  }
]);